<template>
  <div class="mine-redesign">
    <header class="mine-redesign__hero">
      <button
        v-if="showBackImg"
        type="button"
        class="mine-redesign__back"
        :aria-label="$t('app.nav.qadetail')"
        @click="onBackClick"
      >
        <img :src="require('@/assets/back_arrow.png')" alt="" />
      </button>

      <div v-if="showTopContentFlag" class="mine-redesign__profile">
        <div class="mine-redesign__avatar" aria-hidden="true">
          <img :src="require('@/assets/header.png')" alt="" />
        </div>
        <div class="mine-redesign__profile-body">
          <p class="mine-redesign__label">{{ $t("app.mine.username") }}</p>
          <p class="mine-redesign__name">{{ userName }}</p>
          <p v-if="roleDesc" class="mine-redesign__role">{{ roleDesc }}</p>
        </div>
        <button
          type="button"
          class="mine-redesign__switch"
          @click="onChangeUser"
        >
          {{ $t("app.mine.changerole") }}
        </button>
      </div>
    </header>

    <div class="mine-redesign__scroll">
      <div class="mine-redesign__menu">
        <NewMineItemRedesign
          v-for="(item, index) in mineItemList"
          :key="index"
          :mineItemList="item"
        />
      </div>
    </div>

    <Docker :currentIndex="3" />

    <SelectRolePopupRedesign
      v-if="showAccountPopupFlag"
      v-model:showAccountPopupFlag="showAccountPopupFlag"
      :roleList="roleList"
      @changeRole="handleItemClick"
    />
  </div>
</template>

<script>
import Docker from "@/components/Docker";
import NewMineItemRedesign from "@/views/mine/NewMineItemRedesign.vue";
import SelectRolePopupRedesign from "@/components/SelectRolePopupRedesign";
import { useMinePage } from "./useMinePage";

export default {
  name: "MineRedesign",
  components: {
    Docker,
    NewMineItemRedesign,
    SelectRolePopupRedesign,
  },
  setup() {
    return useMinePage();
  },
};
</script>

<style lang="scss" scoped>
@import "@/style/mixins.scss";

$green: #009343;
$green-deep: #007a3a;
$green-light: #e8f5ed;
$page-bg: #f4f7f5;
$surface: #ffffff;
$ink: #1a1f1c;
$label: #8a9490;
$card-radius: 0.12rem;
$btn-radius: 0.16rem;
$shadow-card: 0 0.02rem 0.08rem rgba(13, 40, 24, 0.08);
$ease-out: cubic-bezier(0.22, 1, 0.36, 1);

.mine-redesign {
  --mine-gutter: 0.16rem;

  position: relative;
  height: 100%;
  min-height: 0;
  overflow: hidden;
  background: $page-bg;
  display: flex;
  flex-direction: column;
  text-align: left;
  padding-bottom: 0.49rem;
  box-sizing: border-box;
  isolation: isolate;

  &::before {
    content: "";
    position: absolute;
    inset: 0;
    pointer-events: none;
    z-index: 0;
    background:
      radial-gradient(
        ellipse 130% 42% at 50% -8%,
        rgba(232, 245, 237, 0.95) 0%,
        transparent 58%
      ),
      linear-gradient(
        180deg,
        rgba(232, 245, 237, 0.55) 0%,
        rgba(244, 247, 245, 0.18) 22%,
        transparent 38%
      );
  }

  &::after {
    content: "";
    position: absolute;
    right: -0.24rem;
    bottom: 0.72rem;
    width: 1.5rem;
    height: 1.5rem;
    pointer-events: none;
    z-index: 0;
    border-radius: 50%;
    background: radial-gradient(
      circle,
      rgba(0, 147, 67, 0.1) 0%,
      transparent 72%
    );
  }

  &__hero {
    flex-shrink: 0;
    position: relative;
    z-index: 1;
    overflow: hidden;
    background: linear-gradient(180deg, $green 0%, $green-deep 100%);
    padding: calc(0.2rem + env(safe-area-inset-top, 0px)) var(--mine-gutter)
      0.36rem;

    &::before {
      content: "";
      position: absolute;
      top: -0.36rem;
      right: -0.18rem;
      width: 1.1rem;
      height: 1.1rem;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.1);
      pointer-events: none;
    }

    &::after {
      content: "";
      position: absolute;
      left: -0.28rem;
      bottom: -0.42rem;
      width: 1.3rem;
      height: 1.3rem;
      border-radius: 50%;
      background: rgba(255, 255, 255, 0.06);
      pointer-events: none;
    }
  }

  &__back {
    position: absolute;
    top: calc(0.12rem + env(safe-area-inset-top, 0px));
    left: var(--mine-gutter);
    z-index: 2;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 0.34rem;
    height: 0.34rem;
    padding: 0;
    border: none;
    border-radius: 50%;
    background: rgba(255, 255, 255, 0.16);
    cursor: pointer;

    img {
      width: 0.18rem;
      height: 0.18rem;
    }
  }

  &__profile {
    position: relative;
    z-index: 1;
    display: flex;
    align-items: center;
    gap: 0.12rem;
    padding: 0.14rem 0.16rem;
    border-radius: $card-radius;
    background: rgba(255, 255, 255, 0.14);
    border: 0.01rem solid rgba(255, 255, 255, 0.22);
    box-shadow: 0 0.08rem 0.2rem rgba(2, 60, 30, 0.12);
    text-align: left;
  }

  &__avatar {
    flex-shrink: 0;
    width: 0.56rem;
    height: 0.56rem;
    border-radius: 50%;
    overflow: hidden;
    background: rgba(255, 255, 255, 0.2);
    border: 0.02rem solid rgba(255, 255, 255, 0.35);

    img {
      display: block;
      width: 100%;
      height: 100%;
      object-fit: cover;
    }
  }

  &__profile-body {
    flex: 1;
    min-width: 0;
  }

  &__label {
    margin: 0;
    font-size: 0.11rem;
    font-weight: 500;
    color: rgba(255, 255, 255, 0.82);
    line-height: 1.4;
  }

  &__name {
    margin: 0.04rem 0 0;
    font-size: 0.16rem;
    font-weight: 600;
    color: #ffffff;
    line-height: 1.35;
    overflow-wrap: anywhere;
  }

  &__role {
    margin: 0.06rem 0 0;
    font-size: 0.11rem;
    color: rgba(255, 255, 255, 0.78);
    line-height: 1.45;
    overflow-wrap: anywhere;
  }

  &__switch {
    flex-shrink: 0;
    padding: 0.06rem 0.1rem;
    border: 0.01rem solid rgba(255, 255, 255, 0.55);
    border-radius: $btn-radius;
    background: rgba(255, 255, 255, 0.12);
    color: #ffffff;
    font-size: 0.11rem;
    font-weight: 600;
    line-height: 1.3;
    cursor: pointer;
    transition: background 0.15s $ease-out;

    &:active {
      background: rgba(255, 255, 255, 0.22);
    }
  }

  &__scroll {
    position: relative;
    z-index: 1;
    flex: 1;
    min-height: 0;
    @include smooth-scroll;
    margin-top: -0.12rem;
    padding: 0.18rem var(--mine-gutter) calc(0.16rem + env(safe-area-inset-bottom, 0px));
    background: linear-gradient(
      180deg,
      rgba(255, 255, 255, 0.42) 0%,
      transparent 0.14rem
    );
  }

  &__menu {
    display: flex;
    flex-direction: column;
    gap: 0.12rem;
    width: 100%;
    padding-top: 0.04rem;
  }
}

@media (min-width: 768px) {
  .mine-redesign {
    --mine-gutter: 0.2rem;

    &__profile,
    &__menu {
      text-align: left;
    }

    &__name,
    &__role {
      text-align: left;
    }
  }
}

@media (prefers-reduced-motion: reduce) {
  .mine-redesign__switch {
    transition: none;
  }
}
</style>
